import { useNavigate } from "react-router-dom";
import { Reveal } from "./Reveal";

const external = (html) => html.replace(/<a href="(https?:)/g, '<a target="_blank" rel="noreferrer" href="$1');

const Html = ({ as: Tag = "div", html, className }) => (
  <Tag className={className} dangerouslySetInnerHTML={{ __html: external(html) }} />
);

const headingSize = { 1: "text-3xl sm:text-4xl lg:text-5xl", 2: "text-2xl sm:text-3xl", 3: "text-xl sm:text-2xl", 4: "text-lg sm:text-xl" };

const Block = ({ b }) => {
  if (b.type === "heading")
    return <Html as="h2" html={b.html} className={`font-display font-bold leading-tight text-white mt-16 mb-2 ${headingSize[b.level] || headingSize[3]}`} />;
  if (b.type === "text")
    return <Html as="p" html={b.html} className={b.small ? "text-sm text-zinc-400" : "text-base sm:text-lg leading-relaxed text-zinc-300"} />;
  if (b.type === "list")
    return (
      <Html
        as={b.ordered ? "ol" : "ul"}
        html={b.items.map((i) => `<li>${i}</li>`).join("")}
        className={`space-y-2 pl-6 text-base sm:text-lg text-zinc-300 marker:text-lime ${b.ordered ? "list-decimal" : "list-disc"}`}
      />
    );
  if (b.type === "image")
    return (
      <Reveal y={30}>
        <figure className="my-6 mx-auto w-fit max-w-full rounded-3xl overflow-hidden border border-white/10 bg-panel">
          <img src={b.src} alt={b.alt} loading="lazy" className="block max-w-full max-h-[70vh] w-auto h-auto" />
        </figure>
      </Reveal>
    );
  if (b.type === "youtube")
    return (
      <Reveal y={30}>
        <div className="my-6 relative rounded-3xl overflow-hidden border border-white/10 bg-panel aspect-video">
          <iframe className="absolute inset-0 w-full h-full" src={`https://www.youtube.com/embed/${b.id}`} title={b.title || "YouTube video"} loading="lazy" allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
        </div>
      </Reveal>
    );
  if (b.type === "button")
    return (
      <a href={b.href} target={b.href?.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className="inline-flex self-start rounded-full bg-lime text-ink font-mono text-[12px] uppercase tracking-[0.2em] px-6 py-3 hover:bg-white transition-colors duration-300">
        {b.label}
      </a>
    );
  return null;
};

export const TopicBody = ({ blocks }) => {
  const navigate = useNavigate();
  const onClick = (e) => {
    const a = e.target.closest("a");
    const href = a?.getAttribute("href");
    if (href && href.startsWith("/") && !a.target) {
      e.preventDefault();
      navigate(href);
    }
  };
  return (
    <article data-testid="topic-body" onClick={onClick} className="topic-body flex flex-col gap-5 max-w-3xl">
      {blocks.map((b, i) => <Block key={i} b={b} />)}
    </article>
  );
};
